import customHttp from "../services/customHTTP";
import { globalNavigate } from "./globalNavigation";

const KNOWN_AXIOS_CONFIG_KEYS = new Set([
	"headers",
	"params",
	"timeout",
	"onUploadProgress",
	"withCredentials",
	"responseType",
	"signal",
	"auth",
	"skipAuth",
]);

const toLowerText = (value) =>
	typeof value === "string" ? value.toLowerCase() : "";

const normalizeConfig = (options) => {
	if (!options || typeof options !== "object") return {};
	const keys = Object.keys(options);
	const hasKnownKey = keys.some((key) => KNOWN_AXIOS_CONFIG_KEYS.has(key));

	if (hasKnownKey) {
		const normalized = { ...options };
		if (normalized.headers && typeof normalized.headers !== "object") {
			normalized.headers = {};
		}
		return normalized;
	}

	// Treat bare objects as header maps for backward compatibility
	return {
		headers: { ...options },
	};
};

// Every real error response this app's own backend ever sends (HTTP400/401/403/500 —
// see utilities/httpResponses.js) always carries a JSON body with at least a `message`.
// A response that comes back with NO body at all is therefore never a genuine
// application-level rejection — live-reproduced (2026-07-22): CHIP-driven navigation to
// /cart or /requests intermittently got a bare, bodyless 400 on getCart/byids/bysiteid
// that a plain page refresh immediately resolved, same shape as the earlier
// workorder-report-link 400 (see ChatBot.js's /assets/ pre-warm retry) — something
// transient in front of our own app, not a rejection from it. Retrying ONLY on this exact
// "server responded, but sent no body" signature can never mask a real validation/
// permission error (those always have a message and are returned as-is below, unmodified).
const isBodylessFailure = (error) =>
	Boolean(error?.response) && !error.response.data;

const MAX_ATTEMPTS = 3;
const RETRY_DELAY_MS = 400;

export const handleRequest = async (
	method,
	url,
	data = null,
	options = {},
	responseType = "json"
) => {
	const verb = method.toLowerCase();
	const normalized = normalizeConfig(options);
	const config = {
		responseType: normalized.responseType || responseType,
		...normalized,
	};
	if (!config.headers) config.headers = {};

	let requestData = data;
	if (verb === "get") {
		if (requestData !== null && requestData !== undefined) {
			config.params = requestData;
		}
		requestData = undefined;
	}

	for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
		try {
			let response;
			if (verb === "get") {
				response = await customHttp[verb](url, config);
			} else if (verb === "delete") {
				response = await customHttp.delete(url, { ...config, data: requestData });
			} else {
				response = await customHttp[verb](url, requestData, config);
			}

			//FIX: Always return response.data (works for all response types)
			return response.data;

		} catch (error) {
			if (isBodylessFailure(error) && attempt < MAX_ATTEMPTS) {
				// eslint-disable-next-line no-await-in-loop
				await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS * attempt));
				// eslint-disable-next-line no-continue
				continue;
			}

			console.error(error);


			if (error.response?.status === 403) {
				return error.response.data;
			}

			if (error.config?.skipAuth && error.response?.status === 401) {
				return error.response.data;
			}

			const errorText = toLowerText(error.response?.data?.error);
			const messageText = toLowerText(error.response?.data?.message);
			if (errorText.includes("permission") || messageText.includes("permission")) {
				return error.response.data;
			}

			if (
				error.response?.status === 401 ||
				error.response?.data?.message === "Session expired or invalid" ||
				error.response?.data?.message === "Invalid or expired JWT token"
			) {
				localStorage.clear();
				if (window.location.pathname !== "/login") {
					globalNavigate("/login", { external: true });
				}
				return {};
			}

			return error.response?.data || { error: "Unknown error" };
		}
	}
};
